import type { PersistedConsentState } from "../api/types";

const STORAGE_KEY = "redacto_consent_sdk_state";
const STATE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

const isBrowser = (): boolean => {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
};

export const saveConsentState = (state: PersistedConsentState): void => {
  if (!isBrowser()) {
    console.warn("SessionStorage not available - state not persisted");
    return;
  }

  try {
    const stateWithTimestamp = {
      ...state,
      timestamp: new Date().toISOString(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithTimestamp));
  } catch (error) {
    console.error("Failed to save consent state:", error);
  }
};

export const getConsentState = (): PersistedConsentState | null => {
  if (!isBrowser()) return null;

  try {
    const savedState = sessionStorage.getItem(STORAGE_KEY);
    if (!savedState) return null;

    const parsed: PersistedConsentState = JSON.parse(savedState);

    const stateAge = Date.now() - new Date(parsed.timestamp).getTime();
    if (stateAge > STATE_EXPIRY_MS) {
      clearConsentState();
      return null;
    }

    return parsed;
  } catch (error) {
    console.error("Failed to get consent state:", error);
    return null;
  }
};

export const clearConsentState = (): void => {
  if (!isBrowser()) return;

  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear consent state:", error);
  }
};

export const updateConsentState = (updates: Partial<PersistedConsentState>): void => {
  const currentState = getConsentState();
  if (currentState) {
    saveConsentState({
      ...currentState,
      ...updates,
      timestamp: new Date().toISOString(),
    });
  }
};

export const hasPendingVerification = (): boolean => {
  const state = getConsentState();
  return state !== null && state.flowState === "digilocker-pending";
};

export const getVerificationResult = (): "pending" | "verified" | "failed" | "in_progress" | "expired" | null => {
  const state = getConsentState();
  return state?.verificationResult ?? null;
};

export const getVerificationReference = (): string | null => {
  const state = getConsentState();
  return state?.verificationReference ?? null;
};
